'use client';
// PORTED from tkt-new-ui app/boxoffice/_components/printer-setup — keep the two in step. Only the
// imports differ: dialog/button/toast/hook come from ./shims, which map them onto this console.

/**
 * PRINTER SETUP — a walk-through that MEASURES the printer in front of it instead of assuming.
 *
 * Why this exists: every setting the till used to rely on was a belief carried over from one unit.
 * The cut offset (626) was tuned on a printer someone had switched into black-mark mode with the
 * vendor tool; the next printer was still in continuous mode, so it accepted every mark command and
 * did nothing — and nothing on screen could say so, because nothing ever asked the printer. The QR
 * command was likewise the one that one firmware prints; the same USB ID ships with a firmware that
 * prints a different one. See ~/printer/KNOWLEDGE.md.
 *
 * So each step either READS something from the printer (raw bytes shown) or PRINTS something the
 * operator reads back (numbers printed on the paper, not judgement calls), and writes down what it
 * found as evidence on a profile bound to THAT device. The wire log under every step shows every
 * byte, both ways, so "what did it actually say?" always has an answer.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './shims';
import { Button } from './shims';
import { cn } from './shims';
import { toast } from './shims';
import { useMasungPrinter } from './shims';
import { connectWithDiagnosis } from '../../../lib/ticketPrinter/connectFailure';
import {
  emptyProfile, recognize, deviceKey, missingEssentials, CHECK_NAMES, listProfiles,
  type PrinterProfile, type CheckRecord, type CheckStatus,
} from '../../../lib/masung-printer/profile';
import { decodeStatus, rawHex, worstSeverity, STATUS_QUERY_NAMES, type StatusReading } from '../../../lib/masung-printer/status';
import {
  analyzeTrace, cutCorrectionMm, paperSpanFromColumns, statusTraceSignal, offsetUnitsFromMm, mmFromOffsetUnits, cutterGapMm, WIDTH_CANDIDATES,
  type TraceSample, type TraceAnalysis,
} from '../../../lib/masung-printer/calibration';
import type { PaperType } from '../../../lib/masung-printer/commands';
import type { MasungTicketData } from '../../../lib/masung-printer/printer';
import { activeSim } from '../../../lib/masung-printer/simulator';
import { Choice, KV, Note, NumberField, StatusIcon, StepHeader, TraceChart, Verdict, WireLog, useTick } from './ui';

const STEPS = [
  { id: 'env', title: 'Browser' },
  { id: 'connect', title: 'Connect' },
  { id: 'talk', title: 'Two-way talk' },
  { id: 'health', title: 'Health' },
  { id: 'selftest', title: "Printer's own settings" },
  { id: 'capability', title: 'What it can print' },
  { id: 'paper', title: 'Paper type & sensor' },
  { id: 'cut', title: 'Cut position' },
  { id: 'verify', title: 'Real tickets' },
  { id: 'done', title: 'Summary' },
] as const;
type StepId = (typeof STEPS)[number]['id'];

const now = () => new Date().toISOString();
const rec = (status: CheckStatus, evidence: string): CheckRecord => ({ status, at: now(), evidence });

export default function PrinterSetupWizard({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const masung = useMasungPrinter();
  const printer = masung.printer;
  const connected = masung.status.connected;
  const [step, setStep] = useState<StepId>('env');
  const [draft, setDraft] = useState<PrinterProfile | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showLog, setShowLog] = useState(true);
  useTick(500);

  // The draft follows the DEVICE: a different printer gets its own profile, never this one's.
  const identity = connected ? printer.identity() : null;
  const key = identity ? deviceKey(identity) : null;
  useEffect(() => {
    if (!identity || !key) return;
    if (draft?.key === key) return;
    setDraft(masung.profile?.key === key ? masung.profile : emptyProfile(identity));
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Record a finding: merge, persist, put in force. */
  // A ref, so persistence happens once per finding in ordinary code — not inside a state updater,
  // which React may run twice (the hook's own settings had exactly that bug).
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const saveProfile = masung.saveProfile;
  const update = useCallback((partial: Partial<PrinterProfile>, check?: [StepId | string, CheckRecord]) => {
    const d = draftRef.current;
    if (!d) return;
    const merged: PrinterProfile = { ...d, ...partial, checks: { ...d.checks, ...(partial.checks ?? {}), ...(check ? { [check[0]]: check[1] } : {}) } };
    // "Set up" is a CONSEQUENCE of the essential checks, recomputed on every finding — never a flag
    // a screen sets. Re-running a step that now fails takes the printer back out of "set up".
    const complete = missingEssentials(merged).length === 0;
    merged.verifiedAt = complete ? (merged.verifiedAt ?? now()) : undefined;
    const next = saveProfile(merged);
    draftRef.current = next;
    setDraft(next);
  }, [saveProfile]);

  /** Run a printer action with its progress and failure visible on the step, not only in a toast. */
  const run = useCallback(async <T,>(label: string, fn: () => Promise<T>): Promise<T | undefined> => {
    setBusy(label); setError(null);
    try {
      /**
       * NEVER PRINT INTO AN OFFLINE PRINTER. ✅ FIELD: the EP802 went offline (DLE EOT 1 = 0x1E)
       * after failing to find a black mark, accepted the whole check slip, and printed nothing —
       * the operator saw bytes go out and no paper. Ask first; refuse with the reason.
       */
      if (/^Print/.test(label)) {
        const raw = await printer.readStatusRaw();
        if (raw[0] !== null && raw[0] & 0x08) {
          const causes = raw.map((b, i) => decodeStatus((i + 1) as 1 | 2 | 3 | 4 | 5, b))
            .flatMap((r) => r.flags.filter((f) => f.severity === 'error').map((f) => f.label + (f.fix ? ` — ${f.fix}` : '')));
          throw new Error(`the printer is OFFLINE — it would HOLD this job and print it by surprise once the error clears (status ${raw.map(rawHex).join(' ')}).\n• ${causes.join('\n• ')}`);
        }
      }
      return await fn();
    }
    catch (e: any) { setError(`${label} failed: ${e?.message ?? e}`); return undefined; }
    finally { setBusy(null); }
  }, [printer]);

  const envCheck = useMemo(() => {
    if (typeof window === 'undefined') return undefined;
    const usb = 'usb' in navigator || printer.isSimulated;
    const secure = window.isSecureContext;
    return usb && secure
      ? rec('pass', `WebUSB available${printer.isSimulated ? ' (SIMULATED printer)' : ''}; secure context (${location.protocol}//${location.host}).`)
      : rec('fail', [!usb && 'This browser has no WebUSB — use Google Chrome or Microsoft Edge.', !secure && 'Not a secure context — open the till over https:// or on localhost.'].filter(Boolean).join(' '));
  }, [printer.isSimulated]);

  const checkFor = (id: StepId): CheckRecord | undefined =>
    id === 'env' ? envCheck
      : id === 'connect' ? (connected ? rec('pass', '') : undefined)
      : id === 'cut' && draft?.paperType === 'continuous' ? draft?.checks.blade
      : draft?.checks[id];

  const idx = STEPS.findIndex((s) => s.id === step);
  const needsDevice = !['env', 'connect'].includes(step);
  const sim = activeSim();

  const ctx: Ctx = { masung, printer, draft, update, run, busy, connected };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[1180px] w-[96vw] h-[92vh] p-0 gap-0 overflow-hidden flex flex-col sm:max-w-[1180px]">
        <div className="flex items-center justify-between border-b px-5 py-3">
          <div>
            <DialogTitle className="text-[16px]">Printer setup</DialogTitle>
            <DialogDescription className="text-[12px]">
              {identity ? `${identity.productName || 'USB printer'} · ${key}` : 'No printer connected yet'}
              {printer.isSimulated && <span className="ml-2 rounded bg-amber-500/20 px-1.5 py-0.5 font-semibold text-amber-800">SIMULATOR</span>}
            </DialogDescription>
          </div>
        </div>

        <div className="flex min-h-0 flex-1">
          {/* Steps */}
          <nav className="w-56 shrink-0 border-r overflow-y-auto py-2">
            {STEPS.map((s, i) => {
              const c = checkFor(s.id);
              return (
                <button
                  key={s.id}
                  onClick={() => setStep(s.id)}
                  className={cn('flex w-full items-center gap-2 px-4 py-2 text-left text-[13px]', step === s.id ? 'bg-muted font-semibold' : 'hover:bg-muted/50')}
                >
                  <StatusIcon status={busy && step === s.id ? 'busy' : c?.status} />
                  <span className="text-muted-foreground w-4">{i + 1}</span>
                  <span>{s.title}</span>
                </button>
              );
            })}
          </nav>

          {/* Step body */}
          <main className="min-w-0 flex-1 overflow-y-auto px-6 py-5">
            {/* The step stays MOUNTED while the printer is away: storing a setting restarts it, and
                unmounting here threw away the slip readings and attempt history mid-calibration. */}
            {needsDevice && !connected && (
              <div className="mb-4"><Note tone="warn">{busy ? 'The printer is restarting to store a setting — it comes back by itself.' : <>No printer is connected. Go back to <b>Connect</b>.</>}</Note></div>
            )}
            {(!needsDevice || connected || draft) && (
              <>
                {step === 'env' && <EnvStep check={envCheck} />}
                {step === 'connect' && <ConnectStep {...ctx} />}
                {step === 'talk' && <TalkStep {...ctx} />}
                {step === 'health' && <HealthStep {...ctx} />}
                {step === 'selftest' && <SelfTestStep {...ctx} />}
                {step === 'capability' && <CapabilityStep {...ctx} />}
                {step === 'paper' && <PaperStep {...ctx} />}
                {step === 'cut' && <CutStep {...ctx} />}
                {step === 'verify' && <VerifyStep {...ctx} />}
                {step === 'done' && <DoneStep {...ctx} />}
              </>
            )}
            {busy && <div className="mt-4 flex items-center gap-2 text-[13px] text-muted-foreground"><StatusIcon status="busy" /> {busy}…</div>}
            {error && <div className="mt-4 whitespace-pre-wrap"><Note tone="error">{error}</Note></div>}
          </main>

          {sim && (
            <aside className="w-72 shrink-0 border-l bg-muted/30 flex flex-col">
              <div className="border-b px-3 py-2 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Simulated paper</div>
              <pre className="flex-1 overflow-y-auto px-3 py-2 text-[10px] leading-[1.35] font-mono whitespace-pre-wrap break-all">
                {sim.paperView(420).join('\n') || '(nothing printed)'}
              </pre>
              <div className="border-t px-3 py-2 text-[11px] text-muted-foreground">
                Stored in sim: paper={sim.nv.paperType} cut={sim.nv.cut} print={sim.nv.print}
              </div>
            </aside>
          )}
        </div>

        {/* Wire log */}
        <div className={cn('border-t bg-muted/20 flex flex-col', showLog ? 'h-40' : 'h-9')}>
          <div className="flex items-center justify-between px-3 h-9 shrink-0">
            <button className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground" onClick={() => setShowLog((v) => !v)}>
              {showLog ? '▾' : '▸'} Every byte sent (→) and received (←)
            </button>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" className="h-7 text-xs" disabled={idx === 0} onClick={() => setStep(STEPS[idx - 1].id)}>Back</Button>
              <Button size="sm" className="h-7 text-xs" disabled={idx === STEPS.length - 1} onClick={() => setStep(STEPS[idx + 1].id)}>Next</Button>
            </div>
          </div>
          {showLog && <div className="min-h-0 flex-1"><WireLog entries={printer.wire} /></div>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ═════════════════════════════════════════════════════════════════════════════════════════════════

interface Ctx {
  masung: ReturnType<typeof useMasungPrinter>;
  printer: ReturnType<typeof useMasungPrinter>['printer'];
  draft: PrinterProfile | null;
  update: (p: Partial<PrinterProfile>, check?: [string, CheckRecord]) => void;
  run: <T>(label: string, fn: () => Promise<T>) => Promise<T | undefined>;
  busy: string | null;
  connected: boolean;
}

function EnvStep({ check }: { check?: CheckRecord }) {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  return (
    <div>
      <StepHeader title="Can this browser reach a USB printer?" why="The till talks to the printer directly over USB from the browser. That needs Chrome or Edge, and an https:// (or localhost) address." />
      <Verdict check={check} />
      <div className="mt-4 space-y-2">
        {/Windows/.test(ua) && (
          <Note tone="warn">
            <b>Windows:</b> if the printer was installed as a normal Windows printer (“USB Printing Support”), Windows holds it and the
            browser cannot. Either remove that printer and switch its driver to WinUSB (Zadig), or keep the Windows driver and use Masung&apos;s
            WebPrintService instead.
          </Note>
        )}
        {/Mac OS X/.test(ua) && (
          <Note>
            <b>Mac:</b> if this printer appears under System Settings → Printers, remove it there. macOS otherwise holds the device and the browser
            reports it as busy.
          </Note>
        )}
        <Note>Connect the printer <b>directly</b> with a data cable, not through a hub or a charge-only lead. Use one tab only — two tabs cannot share a printer.</Note>
      </div>
    </div>
  );
}

function ConnectStep({ masung, printer, connected, run, draft }: Ctx) {
  const [failure, setFailure] = useState<string | null>(null);
  const id = printer.identity();
  const r = id ? recognize(id) : null;
  const hex4 = (n: number) => '0x' + n.toString(16).padStart(4, '0');
  return (
    <div>
      <StepHeader title="Connect the printer" why="Pick the printer in the browser's list. Then this shows exactly what the device says it is — the model is not assumed from anything else." />
      {!connected && (
        <Button onClick={() => run('Opening the printer chooser', async () => { setFailure(await connectWithDiagnosis(() => masung.connect())); })}>
          Choose printer…
        </Button>
      )}
      {failure && <div className="mt-3"><Note tone="error">{failure}</Note></div>}
      {connected && id && r && (
        <div className="space-y-3">
          <KV rows={[
            ['USB vendor : product', `${hex4(id.vendorId)} : ${hex4(id.productId)}`],
            ['Product name', id.productName ?? ''],
            ['Manufacturer', id.manufacturerName ?? ''],
            ['Serial number', id.serialNumber ?? ''],
            ['Interface class / protocol', `${id.interfaceClass ?? '?'} / ${id.interfaceProtocol ?? '?'}  (7/2 = two-way USB printer)`],
          ]} />
          <Note tone={r.family === 'unknown' ? 'warn' : 'info'}>
            <div className="font-medium">{r.summary}</div>
            {r.notes.map((n) => <div key={n} className="mt-1 text-muted-foreground">• {n}</div>)}
          </Note>
          {draft?.verifiedAt && <Note>This printer was set up and verified on {new Date(draft.verifiedAt).toLocaleString()}. Re-running any step updates its profile.</Note>}
          <Button variant="outline" size="sm" onClick={() => run('Switching printer', async () => { await masung.disconnect(); setFailure(await connectWithDiagnosis(() => masung.connect())); })}>
            Use a different printer
          </Button>
        </div>
      )}
    </div>
  );
}

function TalkStep({ printer, update, run, draft }: Ctx) {
  const [raw, setRaw] = useState<(number | null)[] | null>(null);
  const ask = () => run('Asking the printer', async () => {
    const s = await printer.readStatusRaw();
    const ident = await printer.readReportedIdentity();
    setRaw(s);
    const answered = s.filter((b) => b !== null).length;
    const identCount = Object.values(ident).filter(Boolean).length;
    update(
      { reported: { ...(draft?.reported ?? {}), ...ident }, model: ident['Model'] || draft?.model },
      ['talk', s[0] !== null
        ? rec('pass', `Status replies: ${s.map(rawHex).join(', ')}. Identity answers: ${identCount}/8.`)
        : rec('warn', `The printer sent nothing back (${answered}/5 status replies). It may still print, but nothing that needs an answer — status, sensor, paper-out — can be checked. Usually a one-way USB mode or a cable/hub issue.`)],
    );
  });
  const asked = useRef(false);
  useEffect(() => { if (!asked.current && !draft?.checks.talk) { asked.current = true; void ask(); } }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div>
      <StepHeader title="Does the printer answer?" why="Printing only needs bytes to go in. Knowing whether paper is out, the lid is open, or a mark was found needs answers to come back. Every reply is shown raw." />
      <Button onClick={ask}>Ask again</Button>
      {raw && (
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <KV rows={raw.map((b, i) => [`DLE EOT ${i + 1} · ${STATUS_QUERY_NAMES[i + 1]}`, rawHex(b)])} />
          <KV rows={Object.entries(draft?.reported ?? {}).map(([k, v]) => [k, v])} />
        </div>
      )}
      <Verdict check={draft?.checks.talk} />
    </div>
  );
}

function HealthStep({ printer, update, run, draft }: Ctx) {
  const [readings, setReadings] = useState<StatusReading[] | null>(null);
  const check = () => run('Reading status', async () => {
    const raw = await printer.readStatusRaw();
    const rs = raw.map((b, i) => decodeStatus((i + 1) as 1 | 2 | 3 | 4 | 5, b, { markMode: draft?.paperType === 'mark' }));
    setReadings(rs);
    const worst = worstSeverity(rs);
    const problems = rs.flatMap((r) => r.flags.filter((f) => f.severity === 'error' || f.severity === 'warn').map((f) => f.label));
    update({}, ['health', worst === 'error'
      ? rec('fail', problems.join('; '))
      : raw.every((b) => b === null)
        ? rec('warn', 'No status replies — health cannot be checked on this connection.')
        : rec(worst === 'warn' ? 'warn' : 'pass', problems.length ? problems.join('; ') : `All clear (${raw.map(rawHex).join(', ')}).`)]);
  });
  const checked = useRef(false);
  useEffect(() => { if (!checked.current) { checked.current = true; void check(); } }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div>
      <StepHeader title="Is the printer ready?" why="Lid, paper, cutter and (on kiosk models) the ticket exit. Each line names the raw byte it came from, and where vendor sources disagree about a bit, it says so." />
      <div className="flex gap-2">
        <Button onClick={check}>Check again</Button>
        <Button variant="outline" onClick={() => run('Recovering from error', async () => { await printer.recover(true); await check(); })}>Clear an error</Button>
      </div>
      {readings && (
        <div className="mt-4 space-y-2">
          {readings.map((r) => (
            <div key={r.query} className="rounded-lg border px-3 py-2 text-[13px]">
              <div className="flex justify-between"><span className="font-medium">{STATUS_QUERY_NAMES[r.query]}</span><span className="font-mono text-muted-foreground">DLE EOT {r.query} → {rawHex(r.raw)}</span></div>
              {r.flags.map((f) => (
                <div key={f.label} className="mt-1 flex gap-2">
                  <StatusIcon status={f.severity === 'ok' || f.severity === 'info' ? 'pass' : f.severity === 'warn' ? 'warn' : 'fail'} className="mt-0.5 h-3.5 w-3.5" />
                  <span>{f.label}{f.fix && <span className="text-muted-foreground"> — {f.fix}</span>}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
      <Verdict check={draft?.checks.health} />
    </div>
  );
}

function SelfTestStep({ printer, update, run, draft }: Ctx) {
  const rep = draft?.reported ?? {};
  const [mode, setMode] = useState<string | undefined>(rep['Self-test paper type']);
  const [cut, setCut] = useState<number | undefined>(rep['Self-test cut offset'] ? Number(rep['Self-test cut offset']) : undefined);
  const [prn, setPrn] = useState<number | undefined>(rep['Self-test print offset'] ? Number(rep['Self-test print offset']) : undefined);
  const save = () => update(
    { reported: { ...rep, 'Self-test paper type': mode ?? '', 'Self-test cut offset': cut?.toString() ?? '', 'Self-test print offset': prn?.toString() ?? '' } },
    ['selftest', mode ? rec(mode === 'not-listed' ? 'warn' : 'pass', `Self-test page says paper type: ${mode}${cut !== undefined ? `, cut offset ${cut}` : ''}${prn !== undefined ? `, print offset ${prn}` : ''}.`) : rec('skipped', 'Self-test not read.')],
  );
  return (
    <div>
      <StepHeader
        title="What does the printer think it is set to?"
        why={<>The printer stores its paper type and mark offsets inside itself, and <b>no command reads them back</b>. The one place they appear is the self-test page it prints. This is how a printer that “worked with 626” differs from one that doesn&apos;t — read it off the paper.</>}
      />
      <Button onClick={() => run('Printing the self-test page', () => printer.selfTest())}>Print self-test page</Button>
      <p className="mt-2 text-[12px] text-muted-foreground">If nothing prints: switch the printer off, hold FEED, switch it on, release after 2 seconds.</p>
      <div className="mt-5 space-y-3">
        <div className="text-[13px] font-medium">What does it say for paper type (may read “Black mark”, “BM”, “Label”, “Gap”, “Continuous”)?</div>
        <p className="text-[12px] text-muted-foreground">EP802 firmware (APP22-…) has no paper-type line. It prints <b>“Joint exploration: Enable/Disable”</b> — the mark/gap sensor. Choose “Not shown” and note it; the sensor trace in step 7 settles it. Also check <b>“Print Width”</b>: a value below your paper width narrows every ticket.</p>
        <Choice value={mode as any} onChange={(v) => setMode(v)} options={[
          { value: 'mark', label: 'Black mark (BM on)' },
          { value: 'gap', label: 'Label / gap' },
          { value: 'continuous', label: 'Continuous / BM off' },
          { value: 'not-listed', label: 'Not shown on the page' },
        ]} />
        <NumberField label="Cut offset printed (if shown)" value={cut} onChange={setCut} />
        <NumberField label="Print offset printed (if shown)" value={prn} onChange={setPrn} />
        <Button onClick={save} disabled={!mode}>Record this</Button>
      </div>
      <Verdict check={draft?.checks.selftest} />
    </div>
  );
}

function CapabilityStep({ printer, update, run, draft }: Ctx) {
  const [letter, setLetter] = useState<string | undefined>();
  const [firstCol, setFirstCol] = useState<number | undefined>();
  const [lastCol, setLastCol] = useState<number | undefined>();
  const [q1, setQ1] = useState<'yes' | 'no' | undefined>();
  const [pic, setPic] = useState<'yes' | 'no' | undefined>();
  const [q3, setQ3] = useState<'yes' | 'no' | undefined>();
  const id = printer.identity();
  const likely = id ? recognize(id).likelyQr : undefined;
  const save = () => {
    const offPaper = letter === 'offpaper' && firstCol !== undefined && lastCol !== undefined;
    const span = offPaper ? paperSpanFromColumns(firstCol!, lastCol!) : null;
    const width = offPaper ? { dots: 832, label: `104 mm head; paper spans columns ${firstCol}–${lastCol} = ${(span!.widthDots / 8).toFixed(0)} mm starting ${(span!.leftDots / 8).toFixed(1)} mm in` } : WIDTH_CANDIDATES.find((c) => c.letter === letter);
    const qrMethod = q1 === 'yes' ? 'escpos' : q3 === 'yes' ? 'dc3' : undefined;
    const lines = [
      width ? `Head ${width.dots} dots (${width.label}).` : 'Head width not read.',
      qrMethod ? `QR: ${qrMethod === 'escpos' ? 'standard ESC/POS (GS ( k)' : 'Masung DC3'} prints.` : 'NEITHER QR command printed a code — tickets would not scan.',
      `Pictures: ${pic === 'yes' ? 'yes' : 'no'}.`,
      likely && qrMethod && likely !== qrMethod ? `Note: the product name suggested ${likely}; the paper says ${qrMethod}. The paper wins.` : '',
    ].filter(Boolean).join(' ');
    update({ headDots: width?.dots, paperLeftDots: span?.leftDots, paperWidthDots: span?.widthDots, qrMethod, rasterWorks: pic === 'yes' }, ['capability', rec(qrMethod ? (width ? 'pass' : 'warn') : 'fail', lines)]);
  };
  return (
    <div>
      <StepHeader title="What can this printer actually print?" why="Two firmwares ship under the same USB ID and print QR codes with different commands. One slip answers it by looking: the head width, which QR prints, and whether pictures print." />
      <Button onClick={() => run('Printing the check slip', () => printer.printCapabilitySlip())}>Print check slip</Button>
      <div className="mt-5 space-y-4">
        <div>
          <div className="text-[13px] font-medium mb-2">1. The top line of dots wraps. What is the <b>last letter on the first line</b>?</div>
          <Choice value={letter} onChange={setLetter} options={[
            ...WIDTH_CANDIDATES.map((c) => ({ value: c.letter as string, label: `Ends with ${c.letter}`, hint: `${c.dots} dots — ${c.label}` })),
            { value: 'offpaper', label: 'The line runs off the paper edges', hint: 'head wider than the paper — read the column ruler' },
            { value: 'none', label: 'Something else / no letter' },
          ]} />
          {letter === 'offpaper' && (
            <div className="mt-3 space-y-2">
              <p className="text-[12px] text-muted-foreground">Under the dotted line are two rows of digits: tens on top, units below. Read the column number (top digit, then bottom digit) of the first and last column that are fully on the paper.</p>
              <NumberField label="First column on the paper (left)" value={firstCol} onChange={setFirstCol} placeholder="e.g. 10" />
              <NumberField label="Last column on the paper (right)" value={lastCol} onChange={setLastCol} placeholder="e.g. 61" />
            </div>
          )}
        </div>
        <YesNo q="2. Under [1] STANDARD QR — is there a proper square QR code?" v={q1} set={setQ1} />
        <YesNo q="3. Under [2] PICTURE — is there a striped rectangle?" v={pic} set={setPic} />
        <YesNo q="4. Under [3] MASUNG QR — is there a proper square QR code?" v={q3} set={setQ3} />
        <p className="text-[12px] text-muted-foreground">A phone camera pointed at a working code shows “TKT PRINTER TEST 1” or “… 3”. Junk characters instead of a code are normal for the method this firmware does not speak.</p>
        <Button onClick={save} disabled={!letter || !q1 || !q3 || !pic || (letter === 'offpaper' && (firstCol === undefined || lastCol === undefined))}>Record this</Button>
      </div>
      <Verdict check={draft?.checks.capability} />
    </div>
  );
}

function YesNo({ q, v, set }: { q: string; v: 'yes' | 'no' | undefined; set: (v: 'yes' | 'no') => void }) {
  return (
    <div>
      <div className="text-[13px] font-medium mb-2">{q}</div>
      <Choice value={v} onChange={set} options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]} />
    </div>
  );
}

function PaperStep({ printer, update, run, draft }: Ctx) {
  const [stock, setStock] = useState<PaperType | undefined>(draft?.paperType);
  const [samples, setSamples] = useState<TraceSample[]>([]);
  const [analysis, setAnalysis] = useState<TraceAnalysis | null>(null);
  const [lengthMm, setLengthMm] = useState<number | undefined>(450);
  const [signal, setSignal] = useState<string | null>(null);
  const [findResult, setFindResult] = useState<{ s2: number | null; s3: number | null } | null>(null);
  const selfTestSays = draft?.reported['Self-test paper type'];
  const written = draft?.checks.paperWrite;

  const write = () => stock && run(`Setting paper type to ${stock} — the printer restarts to store it`, async () => {
    await printer.setPaperType(stock);
    update({ paperType: stock }, ['paperWrite', rec('pass', `Sent 13 74 44 33 ${({ mark: '33', gap: '35', continuous: 'CC' })[stock]} (${stock}); printer restarted and came back.`)]);
    if (stock === 'continuous') update({}, ['paper', rec('pass', 'Plain stock: paper type set to continuous. The cut is positioned by ticket length (next step).')]);
  });

  const trace = () => run('Tracing the paper past the sensor', async () => {
    setSamples([]); setAnalysis(null);
    const got: TraceSample[] = [];
    const s = await printer.sensorTrace(lengthMm ?? 450, 1, (x) => { got.push(x); setSamples([...got]); });
    // No raw readings → find the status bit that follows the paper, and analyse that instead.
    const usesStatus = s.length > 0 && s.every((x) => !Number.isFinite(x.ad));
    const signal = usesStatus ? statusTraceSignal(s) : null;
    setSignal(usesStatus ? (signal ? `${signal.label} (flipped ${signal.flips}×)` : 'no status bit changed along the paper') : null);
    if (signal) setSamples(signal.converted);
    const a = analyzeTrace(signal ? signal.converted : usesStatus ? [] : s);
    setAnalysis(a);
    if (a.verdict === 'marks-found') {
      update({ pitchMm: a.pitchMm ?? undefined }, ['paper', rec('pass', `Sensor sees marks: paper reads ≈ ${a.markIsHigh ? a.min : a.max}, a mark ≈ ${a.markIsHigh ? a.max : a.min}; ${a.marks.length} marks, ${a.pitchMm?.toFixed(1)} mm apart (ticket length).`)]);
    } else if (a.verdict === 'no-readings') {
      update({}, ['paper', rec('fail', usesStatus
        ? `The sensor gives no reading (1B CC 01 silent) and no status bit changed over ${lengthMm} mm — the sensor is not seeing the notches. Check it sits under the notch line, or try the other paper type and "Find a mark".`
        : 'The sensor did not report readings.')]);
    } else {
      update({}, ['paper', rec('fail', a.verdict === 'one-mark'
        ? `Marks are visible but not two full ones in ${lengthMm} mm — trace a longer stretch (more than two tickets) to measure the ticket length.`
        : `No mark seen: readings stayed between ${a.min} and ${a.max}. Check the marks are on the side facing the sensor, or that this roll has marks at all.`)]);
    }
  });

  const findMark = () => run('Feeding to the next mark', async () => setFindResult(await printer.findMark()));

  return (
    <div>
      <StepHeader
        title="What paper is loaded — and does the printer see it?"
        why={<>This is the setting that made the last printer “do nothing”: a printer in continuous mode accepts every mark command and ignores it. It is written to the printer here (code <code>13 74 44 33</code>, which no SDK wraps), then <b>proved</b> by watching the sensor.</>}
      />
      <div className="text-[13px] font-medium mb-2">Look at the back of the ticket roll:</div>
      <Choice value={stock} onChange={setStock} options={[
        { value: 'mark', label: 'Black mark (bar or notch)', hint: 'A black bar on the back — or a die-cut notch/slot at each perforation, which a reflective sensor reads as black' },
        { value: 'gap', label: 'Label / gap (or notch)', hint: 'Gaps between labels — or a notch/slot the printer finds as a gap. Try this if black mark is not found' },
        { value: 'continuous', label: 'Plain roll', hint: 'No marks — perforated or not' },
      ]} />
      <div className="mt-4">
        <label className="block text-[13px] font-medium mb-1">Where is the paper sensor set? (physical — software can’t store this in the printer)</label>
        <input
          className="h-9 w-full rounded-md border bg-background px-2 text-[13px]"
          placeholder="e.g. sensor slid to the centre, directly under the die-cut slot"
          defaultValue={draft?.sensorNote ?? ''}
          onBlur={(e) => { if (e.target.value !== (draft?.sensorNote ?? '')) update({ sensorNote: e.target.value }); }}
        />
        <p className="mt-1 text-[12px] text-muted-foreground">✅ On the Zimbabwe notched stock the sensor position was what made detection work — write down where it sits.</p>
      </div>
      {selfTestSays && stock && selfTestSays !== stock && selfTestSays !== 'not-listed' && draft?.paperType !== stock && (
        <div className="mt-3"><Note tone="warn">The self-test page said <b>{selfTestSays}</b>. The printer must be switched to <b>{stock}</b> or mark commands will be ignored.</Note></div>
      )}
      <div className="mt-3 flex items-center gap-3">
        <Button onClick={write} disabled={!stock}>Set the printer to “{stock ?? '…'}”</Button>
        {written && <span className="text-[12px] text-muted-foreground">Last written: {draft?.paperType}</span>}
      </div>
      <Verdict check={written} />

      {/* The trace reads the raw sensor in ANY mode — so it can answer "does the sensor even see the
          notch?" before a mode is chosen. ✅ FIELD: the Zimbabwe stock has no printed bar, only a
          die-cut slot at each perforation, and the right mode had to be found, not assumed. */}
      {(
        <div className="mt-6 space-y-3">
          <div className="text-[14px] font-semibold">Watch the sensor</div>
          <p className="text-[13px] text-muted-foreground">Feeds the paper 1 mm at a time and reads the mark sensor each step. Marks show as dips (or peaks); the distance between them is your ticket length. Uses about {lengthMm ?? 300} mm of paper.</p>
          <div className="flex items-center gap-3">
            <NumberField label="Length to trace" value={lengthMm} onChange={setLengthMm} suffix="mm" />
            <Button onClick={trace}>Trace the paper</Button>
          </div>
          {signal && <Note>Raw sensor reading not supported on this firmware — tracing status bits. Signal: <b>{signal}</b></Note>}
          {samples.length > 0 && samples.some((x) => Number.isFinite(x.ad)) && <TraceChart samples={samples} analysis={analysis} />}
          {samples.length > 0 && !samples.some((x) => Number.isFinite(x.ad)) && (
            <div className="rounded-lg border px-3 py-2 font-mono text-[11px] max-h-32 overflow-y-auto">
              {samples.slice(-12).map((x) => <div key={x.mm}>{x.mm} mm · DLE EOT 4 {rawHex(x.raw?.[0] ?? null)} · GS r 1 {rawHex(x.raw?.[1] ?? null)}</div>)}
            </div>
          )}
          {analysis && analysis.verdict !== 'no-readings' && (
            <KV rows={[
              ['Readings', `${analysis.min} … ${analysis.max} (threshold ${analysis.threshold})`],
              ['Marks found', `${analysis.marks.length}${analysis.marks[0] ? ` · each ≈ ${(analysis.marks[analysis.marks.length > 1 ? 1 : 0].endMm - analysis.marks[analysis.marks.length > 1 ? 1 : 0].startMm + 1).toFixed(0)} mm long` : ''}`],
              ['Ticket length (mark to mark)', analysis.pitchMm ? `${analysis.pitchMm.toFixed(1)} mm` : ''],
            ]} />
          )}
          {draft?.paperType && draft.paperType !== 'continuous' && <div className="pt-2 flex items-center gap-3">
            <Button variant="outline" onClick={findMark}>Find a mark</Button>
            <span className="text-[12px] text-muted-foreground">Feeds to the next mark and cuts. The paper should move and stop. With notched stock: does the cut land on the perforation each time?</span>
          </div>}
          {findResult && (
            <Note tone={(findResult.s3 ?? 0) & 0x20 ? 'error' : 'info'}>
              Status after the feed: DLE EOT 2 → {rawHex(findResult.s2)}, DLE EOT 3 → {rawHex(findResult.s3)}.{' '}
              {findResult.s3 === null ? 'No reply, so only your eyes can tell.' : (findResult.s3 & 0x20) || (findResult.s3 & 0x04) ? 'The printer reports it did NOT find a mark.' : 'No mark error reported.'}
              <div className="mt-2 flex gap-2">
                <Button size="sm" variant="outline" onClick={() => update({}, ['paper', rec('pass', `Operator saw the paper feed to a mark and stop; status 2/3: ${rawHex(findResult.s2)}/${rawHex(findResult.s3)}.`)])}>It moved and stopped</Button>
                <Button size="sm" variant="outline" onClick={() => update({}, ['paper', rec('fail', `Feed-to-mark: paper did not move/stop as expected; status 2/3: ${rawHex(findResult.s2)}/${rawHex(findResult.s3)}.`)])}>It didn&apos;t</Button>
              </div>
            </Note>
          )}
          <Verdict check={draft?.checks.paper} />
        </div>
      )}
    </div>
  );
}

interface Attempt { command: 'cut33' | 'print11'; units: number; correctionMm: number }

function CutStep({ printer, update, run, draft }: Ctx) {
  const marked = draft?.paperType === 'mark' || draft?.paperType === 'gap';
  const gap = draft?.cutterGapMm;

  // ── Part 1: the blade ──
  const [bladeLast, setBladeLast] = useState<number | null>(null);
  const [bladeCut, setBladeCut] = useState<number | undefined>();
  const printBlade = () => run('Printing the blade slip', async () => {
    const r = await printer.printBladeSlip(60);
    setBladeLast(r.lastLabelMm); setBladeCut(undefined);
  });
  const recordBlade = () => bladeLast !== null && bladeCut !== undefined && update(
    { cutterGapMm: cutterGapMm(bladeLast, bladeCut) },
    ['blade', rec('pass', `Blade is ${cutterGapMm(bladeLast, bladeCut).toFixed(1)} mm past the print head (slip: last line ${bladeLast}, cut at ${bladeCut}).`)],
  );

  // ── Part 2: the cut on marked stock ──
  const [slipLen, setSlipLen] = useState<number | null>(null);
  const [blankAbove, setBlankAbove] = useState<'no' | 'yes' | undefined>();
  const [blankMm, setBlankMm] = useState<number | undefined>();
  const [perf, setPerf] = useState<number | undefined>();
  const [perf2, setPerf2] = useState<number | undefined>();
  const [attempts, setAttempts] = useState<Attempt[]>([]);
  const command = draft?.cutOffsetCommand ?? 'cut33';
  const selfCut = Number(draft?.reported['Self-test cut offset'] || NaN);
  /**
   * THE STORED OFFSET MAY BE UNKNOWN. ✅ FIELD: the EP802 cut correctly with an offset nobody had
   * written this session — whatever an earlier tool left in it. No command reads it back, so it is
   * shown as unknown (this used to display a confident "0") and can be PINNED to a known value, so
   * the same number can be written to the next printer instead of hoping it inherited one.
   */
  const offsetKnown = draft?.cutOffsetUnits !== undefined || Number.isFinite(selfCut);
  const currentUnits = draft?.cutOffsetUnits ?? (Number.isFinite(selfCut) ? selfCut : 0);
  const pitch = draft?.pitchMm ?? (perf !== undefined && perf2 !== undefined ? Math.abs(perf2 - perf) : undefined);
  const backFeedDots = Math.round((gap ?? 0) * 8);
  // The edge (the cut under test) sits at label 0 if the paper reversed, or one blank band above it.
  const edgeLabel = blankAbove === 'yes' ? -(blankMm ?? gap ?? 0) : 0;
  const correction = blankAbove && perf !== undefined ? cutCorrectionMm(edgeLabel, [perf], pitch) : null;
  const proposed = correction !== null ? offsetUnitsFromMm(mmFromOffsetUnits(currentUnits) + correction) : null;

  const resetReading = () => { setBlankAbove(undefined); setPerf(undefined); setPerf2(undefined); };
  const printCut = () => run('Printing the cut slip', async () => {
    const len = Math.min(240, Math.round((pitch ?? 150) + 30));
    await printer.printCutSlip(len, backFeedDots);
    setSlipLen(len); resetReading();
  });

  /**
   * WHICH BYTE MOVES THE CUT is not known in advance: the SDK calls `13 74 33 78` the cut offset,
   * the vendor's own tool labels `13 74 11 78` "cut". So the effect of each change is checked on
   * the next slip — if the cut did not move, the other command is the real one, and that is said.
   */
  const apply = () => proposed !== null && correction !== null && run(`Writing offset ${proposed} — the printer restarts to store it`, async () => {
    let cmd = command;
    const prev = attempts[attempts.length - 1];
    if (prev && Math.abs(prev.correctionMm) > 1.5 && Math.abs(correction - prev.correctionMm) < Math.max(1, Math.abs(prev.correctionMm) / 3)) {
      cmd = command === 'cut33' ? 'print11' : 'cut33';
      toast.info(`The cut did not move after the last change, so this printer takes the other offset (${cmd === 'cut33' ? '13 74 33 78' : '13 74 11 78'}).`);
    }
    if (cmd === 'cut33') await printer.setMarkCutOffset(proposed); else await printer.setMarkPrintOffset(proposed);
    setAttempts((a) => [...a, { command: cmd, units: proposed, correctionMm: correction }]);
    update({ cutOffsetUnits: proposed, cutOffsetCommand: cmd }, ['cut', rec('warn', `Offset set to ${proposed} (${mmFromOffsetUnits(proposed).toFixed(1)} mm) via ${cmd === 'cut33' ? '13 74 33 78' : '13 74 11 78'} after a ${correction.toFixed(1)} mm error. Printing another slip to confirm.`)]);
    const len = slipLen ?? 180;
    await printer.printCutSlip(len, backFeedDots);
    resetReading();
  });

  /** Write a known starting value, then measure from it — the only way to an absolute number. */
  const pin = () => run('Pinning the offset to 0 — the printer restarts to store it', async () => {
    if (command === 'cut33') await printer.setMarkCutOffset(0); else await printer.setMarkPrintOffset(0);
    update({ cutOffsetUnits: 0, cutOffsetCommand: command }, ['cut', rec('warn', `Offset pinned to 0 via ${command === 'cut33' ? '13 74 33 78' : '13 74 11 78'} (it was unknown). Print a cut slip to measure from it.`)]);
    await printer.printCutSlip(slipLen ?? 180, backFeedDots);
    setSlipLen(slipLen ?? 180); resetReading();
  });

  const acceptUnknown = () => correction !== null && update(
    { pitchMm: pitch, backFeedDots: blankAbove === 'no' ? backFeedDots : 0 },
    ['cut', rec('warn', `Cut lands ${Math.abs(correction).toFixed(1)} mm from the perforation with the offset ALREADY STORED in the printer — its value is unknown (never written by this app, cannot be read back). Works on this unit; another printer will not inherit it. Pin it to make this reproducible.`)],
  );

  const accept = () => correction !== null && update(
    { cutOffsetUnits: currentUnits, cutOffsetCommand: command, pitchMm: pitch, backFeedDots: blankAbove === 'no' ? backFeedDots : 0 },
    ['cut', rec('pass', `Cut lands ${Math.abs(correction).toFixed(1)} mm from the perforation with offset ${currentUnits} (${mmFromOffsetUnits(currentUnits).toFixed(1)} mm) via ${command === 'cut33' ? '13 74 33 78' : '13 74 11 78'}. ${blankAbove === 'no' ? `Paper reverses ${gap?.toFixed(1)} mm after the cut, so tickets print from their top edge.` : 'This printer does not reverse paper; tickets start one blade-distance below the edge.'}`)],
  );

  return (
    <div>
      <StepHeader
        title={marked ? 'Land the cut on the perforation' : 'Measure the paper path'}
        why={<>Numbered ruler lines are printed 3 mm apart. You read numbers off the paper, and the settings are calculated from them. Nothing here is a guess like 626.</>}
      />
      {!draft?.paperType && <Note tone="warn">Set the paper type first (previous step).</Note>}

      <section className="space-y-3">
        <div className="text-[14px] font-semibold">1 · Where is the blade?</div>
        <p className="text-[13px] text-muted-foreground">Prints a short ruler and cuts straight after it. The blade is a little way past the print head, so the cut lands a few lines back inside the ruler.</p>
        <Button onClick={printBlade}>Print blade slip</Button>
        {bladeLast !== null && (
          <div className="space-y-2">
            <NumberField label="Number right at the cut" value={bladeCut} onChange={setBladeCut} suffix="mm" placeholder="e.g. 51" />
            <p className="text-[12px] text-muted-foreground">Hold the two pieces together at the cut. If it falls between two lines, type the number in between.</p>
            {bladeCut !== undefined && (
              <Note>Blade is <b>{cutterGapMm(bladeLast, bladeCut).toFixed(1)} mm</b> past the head.
                <div className="mt-2"><Button size="sm" onClick={recordBlade}>Record this</Button></div>
              </Note>
            )}
          </div>
        )}
        <Verdict check={draft?.checks.blade} />
      </section>

      {marked && (
        <section className="mt-8 space-y-3">
          <div className="text-[14px] font-semibold">2 · Where does the cut land?</div>
          <KV rows={[
            ['Offset in force', offsetKnown ? `${currentUnits} units = ${mmFromOffsetUnits(currentUnits).toFixed(1)} mm` : 'UNKNOWN — stored in the printer by an earlier tool; cannot be read back'],
            ['Command used', command === 'cut33' ? '13 74 33 78 (SDK "cut offset")' : '13 74 11 78 (vendor tool "cut paper")'],
            ['Ticket length', pitch ? `${pitch.toFixed(1)} mm` : 'unknown — type two perforations below'],
          ]} />
          {gap === undefined && <Note tone="warn">Do part 1 first — the cut slip needs the blade distance.</Note>}
          <Button onClick={printCut} disabled={gap === undefined}>Print cut slip</Button>
          {slipLen !== null && (
            <div className="space-y-3">
              <p className="text-[13px]">The <b>top edge</b> of the slip is the cut being tested.</p>
              <div>
                <div className="text-[13px] font-medium mb-2">Is there blank paper between the top edge and the “0” line?</div>
                <Choice value={blankAbove} onChange={(v) => { setBlankAbove(v); if (v === 'yes' && blankMm === undefined) setBlankMm(gap); }} options={[
                  { value: 'no', label: 'No — 0 is right at the edge' },
                  { value: 'yes', label: 'Yes, a blank band', hint: 'this printer cannot reverse paper' },
                ]} />
                {blankAbove === 'yes' && <div className="mt-2"><NumberField label="About how tall?" value={blankMm} onChange={setBlankMm} suffix="mm" /></div>}
              </div>
              <NumberField label="Number at the first perforation" value={perf} onChange={setPerf} suffix="mm" placeholder="e.g. 15" />
              {!draft?.pitchMm && <NumberField label="Number at the next perforation" value={perf2} onChange={setPerf2} suffix="mm" />}
              {correction !== null && (
                <Note tone={Math.abs(correction) <= 1.5 ? 'info' : 'warn'}>
                  {Math.abs(correction) <= 1.5
                    ? <>The cut is {Math.abs(correction).toFixed(1)} mm from the perforation — on target.</>
                    : <>The cut lands <b>{Math.abs(correction).toFixed(1)} mm {correction > 0 ? 'before' : 'past'}</b> the perforation.{offsetKnown && <> New offset: {currentUnits} {correction > 0 ? '+' : '−'} {Math.abs(Math.round(correction * 8))} = <b>{proposed}</b>.</>}</>}
                  <div className="mt-2">
                    {!offsetKnown ? (
                      <div className="space-y-2">
                        <p>The offset stored in the printer is unknown, so a new absolute value cannot be calculated from it. Pin it to a known value first — then every later number, and every other printer, can be set exactly.</p>
                        <div className="flex gap-2">
                          <Button size="sm" onClick={pin}>Pin offset to 0 and print a slip</Button>
                          {Math.abs(correction) <= 1.5 && <Button size="sm" variant="outline" onClick={acceptUnknown}>Leave it as found (this printer only)</Button>}
                        </div>
                      </div>
                    ) : Math.abs(correction) > 1.5
                      ? <Button size="sm" onClick={apply}>Apply {proposed} and print another slip</Button>
                      : <Button size="sm" onClick={accept}>This is right — record it</Button>}
                  </div>
                </Note>
              )}
            </div>
          )}
          {attempts.length > 0 && (
            <KV rows={attempts.map((a, i) => [`Attempt ${i + 1}`, `${a.command === 'cut33' ? '13 74 33 78' : '13 74 11 78'} ← ${a.units} after a ${a.correctionMm.toFixed(1)} mm error`])} />
          )}
          <Verdict check={draft?.checks.cut} />
        </section>
      )}
      {!marked && draft?.paperType && (
        <div className="mt-6"><Note>Plain stock has no mark to steer the cut — it is placed by ticket length, and fine-tuned on real tickets in the next step.</Note></div>
      )}
    </div>
  );
}

const sampleTicket = (i: number): MasungTicketData => ({
  eventName: 'PRINTER SETUP', subtitle: 'TEST - NOT VALID', attendeeName: 'TEST', ticketType: 'TEST',
  gate: '-', section: 'A', row: String(i), seat: String(i), date: new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }),
  time: '00:00', entryWindow: '-', venue: '', orderId: `SETUP${i}`, qrData: `TKT PRINTER TEST ${i}`,
});

function VerifyStep({ masung, printer, update, run, draft }: Ctx) {
  const [where, setWhere] = useState<'on' | 'early' | 'late' | undefined>();
  const [byMm, setByMm] = useState<number | undefined>();
  const [scans, setScans] = useState<'yes' | 'no' | undefined>();
  const marked = draft?.paperType === 'mark' || draft?.paperType === 'gap';

  const print3 = () => run('Printing 3 test tickets', async () => {
    masung.beginPrintJob();
    for (let i = 1; i <= 3; i++) await masung.printTicket(sampleTicket(i));
    setWhere(undefined); setScans(undefined);
  });

  /** "Early" = the cut came before the perforation: feed further. Same sign for both stocks. */
  const adjust = () => byMm !== undefined && where && where !== 'on' && run('Adjusting', async () => {
    const delta = (where === 'early' ? 1 : -1) * byMm;
    if (marked) {
      const units = offsetUnitsFromMm(mmFromOffsetUnits(draft?.cutOffsetUnits ?? 0) + delta);
      if ((draft?.cutOffsetCommand ?? 'cut33') === 'cut33') await printer.setMarkCutOffset(units); else await printer.setMarkPrintOffset(units);
      update({ cutOffsetUnits: units });
    } else {
      const wanted = (draft?.tailDots ?? printer.tailDots) + Math.round(delta * 8);
      const tail = Math.max(0, wanted);
      printer.setTailDots(tail);
      update({ tailDots: tail });
      if (wanted < 0) {
        // Said, not clamped quietly: no tail makes a ticket shorter than what is printed on it.
        toast.error(`The printed ticket is ${(-wanted / 8).toFixed(0)} mm longer than the stock's ticket length — it cannot fit. Turn off the header or use longer stock.`);
      }
    }
    toast.success('Adjusted — print the test tickets again');
  });

  const accept = () => {
    const ok = where === 'on' && scans === 'yes';
    update({}, ['verify', ok
      ? rec('pass', 'Three tickets back to back: every cut on its perforation, QR codes scan.')
      : rec('fail', `Cuts: ${where}${byMm ? ` by ${byMm} mm` : ''}; QR scans: ${scans}.`)]);
  };

  return (
    <div>
      <StepHeader title="Print real tickets" why="Three full-layout tickets back to back, the way a sale prints them, using everything measured so far. This is the only proof that counts." />
      {!draft?.qrMethod && (
        <div className="mb-3"><Note tone="error">The QR method has not been established (step 6). Tickets would use the old Masung command, which some firmware prints as junk — do <b>What it can print</b> first.</Note></div>
      )}
      {!marked && <Note>On plain stock the cut is placed by ticket length alone, so tear the roll at a perforation before printing — the first ticket starts wherever the paper is.</Note>}
      <div className="mt-3"><Button onClick={print3} disabled={!draft?.qrMethod}>Print 3 test tickets</Button></div>
      <div className="mt-5 space-y-4">
        <div>
          <div className="text-[13px] font-medium mb-2">Where did the cuts land?</div>
          {!marked && <p className="text-[12px] text-muted-foreground mb-2">On plain stock judge the <b>first</b> cut against its perforation — a wrong ticket length makes later cuts drift further each time.</p>}
          <Choice value={where} onChange={setWhere} options={[
            { value: 'on', label: 'On the perforation, every one' },
            { value: 'early', label: 'Before the perforation', hint: 'a strip of the next ticket is missing' },
            { value: 'late', label: 'After the perforation', hint: 'a strip of the next ticket is attached' },
          ]} />
          {where && where !== 'on' && (
            <div className="mt-3 flex items-center gap-3">
              <NumberField label="By how much?" value={byMm} onChange={setByMm} suffix="mm" />
              <Button size="sm" onClick={adjust} disabled={!byMm}>Adjust</Button>
            </div>
          )}
        </div>
        <YesNo q="Do the QR codes scan with a phone camera (showing “TKT PRINTER TEST”)?" v={scans} set={setScans} />
        <Button onClick={accept} disabled={!where || !scans}>Record result</Button>
      </div>
      <Verdict check={draft?.checks.verify} />
    </div>
  );
}

function DoneStep({ printer, draft, update, run }: Ctx) {
  const missing = draft ? missingEssentials(draft) : [];
  const [others, setOthers] = useState<PrinterProfile[]>([]);
  useEffect(() => { setOthers(listProfiles().filter((p) => p.key !== draft?.key && p.paperType)); }, [draft?.key]);
  if (!draft) return null;
  const offsetPinned = draft.cutOffsetUnits !== undefined;

  /**
   * APPLY ANOTHER PRINTER'S RECIPE — the writes that live IN the printer are replayed (paper type,
   * a pinned offset), the per-ticket choices are copied, and every copied fact is marked as copied,
   * not measured. "Real tickets" must still pass on THIS unit before it counts as set up.
   */
  const applyFrom = (src: PrinterProfile) => run(`Applying ${src.identity.productName ?? 'printer'} settings — the printer restarts for each stored setting`, async () => {
    if (src.paperType) await printer.setPaperType(src.paperType);
    if (src.cutOffsetUnits !== undefined) {
      if ((src.cutOffsetCommand ?? 'cut33') === 'cut33') await printer.setMarkCutOffset(src.cutOffsetUnits); else await printer.setMarkPrintOffset(src.cutOffsetUnits);
    }
    const copied = rec('warn', `Copied from ${src.key} — not measured on this unit. Confirm with Real tickets.`);
    update({
      paperType: src.paperType, markFeed: src.markFeed, qrMethod: src.qrMethod, headDots: src.headDots,
      paperLeftDots: src.paperLeftDots, paperWidthDots: src.paperWidthDots, cutterGapMm: src.cutterGapMm,
      cutOffsetUnits: src.cutOffsetUnits, cutOffsetCommand: src.cutOffsetCommand, backFeedDots: src.backFeedDots,
      pitchMm: src.pitchMm, marginDots: src.marginDots, topDots: src.topDots, tailDots: src.tailDots, sensorNote: src.sensorNote,
      checks: { ...draft.checks, paperWrite: rec('pass', `Paper type ${src.paperType} written (copied from ${src.key}).`), capability: copied, paper: copied, cut: copied, blade: copied, verify: rec('fail', 'Not yet verified on this unit — print Real tickets.') },
    });
    toast.success('Settings applied — now print Real tickets to confirm');
  });

  const report = () => JSON.stringify({ profile: draft, wire: printer.wire.slice(-200), log: printer.log.slice(-100), userAgent: navigator.userAgent }, null, 2);
  const failed = Object.entries(draft.checks).filter(([, c]) => c.status === 'fail');
  return (
    <div>
      <StepHeader title={!missing.length ? 'This printer is set up' : 'Not finished yet'} why={!missing.length
        ? 'Everything below is stored against this printer only. A different printer plugged into this till will ask to be set up rather than borrow these numbers.'
        : 'Some steps have not passed. Tickets will still print with what is recorded, but nothing below "Real tickets" has been proven.'} />
      {missing.length > 0 && <div className="mb-3"><Note tone="error">Still needed before this printer counts as set up: {missing.map((k) => CHECK_NAMES[k] ?? k).join(', ')}.</Note></div>}
      {failed.length > 0 && <div className="mb-3"><Note tone="error">Not working: {failed.map(([k]) => CHECK_NAMES[k] ?? k).join(', ')}</Note></div>}
      <KV rows={[
        ['Printer', `${draft.identity.productName ?? '?'} · ${draft.key}`],
        ['Model (reported)', draft.model ?? ''],
        ['Firmware (reported)', draft.reported['Firmware'] ?? ''],
        ['Head width', draft.headDots ? `${draft.headDots} dots` : ''],
        ['QR method', draft.qrMethod ?? ''],
        ['Paper type', draft.paperType ?? ''],
        ['Cut offset', draft.cutOffsetUnits !== undefined ? `${draft.cutOffsetUnits} (${mmFromOffsetUnits(draft.cutOffsetUnits).toFixed(1)} mm) via ${draft.cutOffsetCommand}` : ''],
        ['Ticket length', draft.pitchMm ? `${draft.pitchMm.toFixed(1)} mm` : ''],
        ['Ticket tail (plain stock)', draft.tailDots !== undefined ? `${draft.tailDots} dots` : ''],
        ['Verified', draft.verifiedAt ? new Date(draft.verifiedAt).toLocaleString() : 'no'],
      ]} />
      <div className="mt-5 text-[14px] font-semibold">Settings recipe</div>
      <p className="text-[12px] text-muted-foreground mb-2">Everything that makes tickets come out right, by where it lives. Reproduce all three groups and a second printer behaves the same.</p>
      <KV rows={[
        ['① Physical — sensor position', draft.sensorNote || '⚠ not recorded (step 7)'],
        ['② Stored in the printer — paper type', draft.paperType ? `${draft.paperType}  (13 74 44 33 ${({ mark: '33', gap: '35', continuous: 'CC' } as const)[draft.paperType]})` : ''],
        ['② Stored in the printer — mark offset', offsetPinned
          ? `${draft.cutOffsetUnits} = ${mmFromOffsetUnits(draft.cutOffsetUnits!).toFixed(1)} mm  (${draft.cutOffsetCommand === 'print11' ? '13 74 11 78' : '13 74 33 78'})`
          : '⚠ UNKNOWN — whatever was already stored; pin it in step 8 to make this reproducible'],
        ['③ Sent with every ticket — mark search', draft.markFeed === 'gsff' ? 'GS FF (1D 0C)' : 'ESC FF (1B 0C)'],
        ['③ Sent with every ticket — QR', draft.qrMethod === 'escpos' ? 'standard GS ( k' : 'Masung DC3 (13 50 48)'],
        ['③ Sent with every ticket — print area', draft.paperWidthDots ? `${(draft.paperWidthDots / 8).toFixed(0)} mm wide, ${((draft.paperLeftDots ?? 0) / 8).toFixed(1)} mm from the head's left end` : draft.headDots ? `${draft.headDots} dots` : ''],
        ['③ Sent with every ticket — reverse after cut', draft.backFeedDots ? `${(draft.backFeedDots / 8).toFixed(1)} mm` : 'none'],
        ['Measured — blade past head', draft.cutterGapMm ? `${draft.cutterGapMm} mm` : ''],
      ]} />
      {others.length > 0 && (
        <div className="mt-4 rounded-lg border p-3">
          <div className="text-[13px] font-medium mb-2">Set this printer up like another one</div>
          {others.map((o) => (
            <div key={o.key} className="flex items-center justify-between gap-3 py-1 text-[13px]">
              <span><b>{o.identity.productName}</b> <span className="font-mono text-[11px] text-muted-foreground">{o.key}</span> · {o.paperType}{o.verifiedAt ? ' · verified' : ''}</span>
              <Button size="sm" variant="outline" onClick={() => applyFrom(o)}>Apply to this printer</Button>
            </div>
          ))}
        </div>
      )}
      <div className="mt-4 space-y-1.5">
        {Object.entries(draft.checks).map(([k, c]) => (
          <div key={k} className="flex gap-2 text-[13px]"><StatusIcon status={c.status} className="mt-0.5" /><span className="font-medium w-44 shrink-0">{CHECK_NAMES[k] ?? k}</span><span className="text-muted-foreground">{c.evidence}</span></div>
        ))}
      </div>
      <div className="mt-4">
        <Button variant="outline" onClick={async () => { await navigator.clipboard.writeText(report()); toast.success('Support report copied'); }}>Copy support report</Button>
      </div>
    </div>
  );
}
